import { useMemo } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { usePetStatuses } from "@/hooks/useLookups";

type PetDeathLike = {
  id?: string;
  death_date?: string | null;
  death_cause?: string | null;
  status?: { name?: string | null } | null;
  owner?: { id?: string | null } | null;
};

type ApplyPetDeathInput = {
  petId: string;
  deathDate: Date;
  deathCause?: string | null;
  queryClient?: QueryClient;
  ownerId?: string | null;
};

export const DECEASED_STATUS_NAME = "Falecido";

export const normalizePetStatusName = (status?: string | null) =>
  status?.trim().toLowerCase() ?? "";

export const isPetDeceased = (pet?: PetDeathLike | null) =>
  normalizePetStatusName(pet?.status?.name) === DECEASED_STATUS_NAME.toLowerCase();

export const useDeceasedPetStatusId = () => {
  const { data: statuses } = usePetStatuses();
  return useMemo(
    () =>
      statuses?.find(
        (status) => normalizePetStatusName(status.name) === DECEASED_STATUS_NAME.toLowerCase(),
      )?.id,
    [statuses],
  );
};

export const getPetDeathSummary = (pet?: PetDeathLike | null) => {
  if (!pet || !isPetDeceased(pet) || !pet.death_date) return null;

  return {
    deathDate: pet.death_date,
    deathCause: pet.death_cause?.trim() || null,
  };
};

export const canCreateFutureAppointmentForPet = (pet?: PetDeathLike | null) =>
  !isPetDeceased(pet);

export const shouldBlockRemindersForPet = (pet?: PetDeathLike | null) =>
  isPetDeceased(pet);

export const fetchFutureAppointmentsForPet = async (
  petId: string,
  fromDate = new Date(),
) => {
  const { data, error } = await supabase
    .from("appointment_pets")
    .select(`
      appointment_id,
      appointment:appointments!inner(
        id,
        starts_at,
        type,
        status,
        deleted_at
      )
    `)
    .eq("pet_id", petId)
    .gt("appointment.starts_at", fromDate.toISOString())
    .in("appointment.status", ["agendado", "confirmado"])
    .is("appointment.deleted_at", null);

  if (error) throw error;

  return (data ?? [])
    .map((row) => row.appointment)
    .filter(Boolean) as Array<{
      id: string;
      starts_at: string;
      type: string;
      status: string;
      deleted_at: string | null;
    }>;
};

export const cancelFutureAppointmentsForPet = async (petId: string) => {
  const appointments = await fetchFutureAppointmentsForPet(petId);

  for (const appointment of appointments) {
    const { error } = await supabase.rpc("soft_delete_appointment", {
      p_appointment_id: appointment.id,
    });
    if (error) throw error;
  }

  return appointments.length;
};

export const applyPetDeath = async ({
  petId,
  deathDate,
  deathCause,
  queryClient,
  ownerId,
}: ApplyPetDeathInput) => {
  const { data: deceasedStatuses, error: lookupError } = await supabase
    .from("pet_statuses")
    .select("id, name")
    .ilike("name", DECEASED_STATUS_NAME);

  if (lookupError) throw lookupError;

  if (!deceasedStatuses || deceasedStatuses.length === 0) {
    throw new Error(`Status de óbito "${DECEASED_STATUS_NAME}" não encontrado.`);
  }

  if (deceasedStatuses.length > 1) {
    throw new Error(`Foram encontrados múltiplos status "${DECEASED_STATUS_NAME}".`);
  }

  const deceasedStatus = deceasedStatuses[0];

  const { error: petDeathError } = await supabase
    .from("pets")
    .update({
      status_id: deceasedStatus.id,
      death_date: deathDate.toISOString().slice(0, 10),
      death_cause: deathCause?.trim() || null,
    })
    .eq("id", petId);

  if (petDeathError) throw petDeathError;

  if (queryClient) {
    queryClient.invalidateQueries({ queryKey: ["pet", petId] });
    invalidatePatientsCache(queryClient, {
      ownerId,
      petIds: [petId],
    });
  }

  return deceasedStatus;
};
